type WsListener = (data: unknown) => void;

export type WsState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface SensorWsConnection {
    connectionId: string;
    listeners: Set<WsListener>;
    refCount: number;
    sensorSerial: string;
    sessionToken: string | null;
    socket: WebSocket | null;
    state: WsState;
    stateListeners: Set<(state: WsState) => void>;
    wsHost: string;
}

const GLOBAL_SENSOR_KEY = '__global__';

const getSensorConnections = () => {
    const win = window as Window & { __sdrSensorWs?: Record<string, SensorWsConnection> };
    if (!win.__sdrSensorWs) {
        win.__sdrSensorWs = {};
    }
    return win.__sdrSensorWs;
};

const createConnectionId = (): string => {
    try {
        return window.crypto.randomUUID();
    } catch {
        return Math.random().toString(16).slice(2) + Date.now().toString(16);
    }
};

const buildWsUrl = (wsHost: string, connectionId: string): string => {
    const base = new URL(`https://${wsHost}`);
    base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
    base.pathname = '/ws';
    base.search = `?id=${encodeURIComponent(connectionId)}`;
    return base.toString();
};

const ensureSensorConnection = (
    wsHost: string,
    sensorSerial: string,
    onStateChange: (state: WsState) => void,
): SensorWsConnection => {
    const connections = getSensorConnections();
    const existing = connections[GLOBAL_SENSOR_KEY];

    const connection: SensorWsConnection =
        existing && existing.wsHost === wsHost
            ? existing
            : {
                  connectionId: createConnectionId(),
                  listeners: new Set<WsListener>(),
                  refCount: 0,
                  sensorSerial,
                  sessionToken: null,
                  socket: null,
                  state: 'idle',
                  stateListeners: new Set<(state: WsState) => void>(),
                  wsHost,
              };

    connections[GLOBAL_SENSOR_KEY] = connection;
    connection.stateListeners.add(onStateChange);

    const { socket } = connection;
    if (!socket || socket.readyState > WebSocket.OPEN) {
        const wsUrl = buildWsUrl(wsHost, connection.connectionId);
        const nextSocket = new WebSocket(wsUrl);
        connection.socket = nextSocket;
        connection.state = 'connecting';
        connection.stateListeners.forEach((listener) => listener('connecting'));

        nextSocket.onopen = () => {
            connection.state = 'open';
            connection.stateListeners.forEach((listener) => listener('open'));
        };

        nextSocket.onclose = () => {
            connection.state = 'closed';
            connection.stateListeners.forEach((listener) => listener('closed'));
        };

        nextSocket.onerror = () => {
            connection.state = 'error';
            connection.stateListeners.forEach((listener) => listener('error'));
        };

        nextSocket.onmessage = (event) => {
            if (typeof event.data !== 'string') {
                return;
            }

            let parsed: unknown;
            try {
                parsed = JSON.parse(event.data);
            } catch {
                return;
            }

            if (!parsed || typeof parsed !== 'object') {
                return;
            }

            const data = parsed as {
                payload?: unknown;
                session?: string;
                token?: string;
                type?: string;
            };

            if (data.type === 'welcome') {
                const token =
                    typeof data.session === 'string'
                        ? data.session
                        : typeof data.token === 'string'
                          ? data.token
                          : null;
                if (token) {
                    connection.sessionToken = token;
                }
            }

            if (data.type === 'ping') {
                try {
                    connection.socket?.send(JSON.stringify({ ts: Date.now() / 1000, type: 'pong' }));
                } catch {
                    // ignore
                }
                return;
            }

            connection.listeners.forEach((listener) => listener(data));
        };
    } else {
        connection.stateListeners.forEach((listener) => listener(connection.state));
    }

    return connection;
};

const releaseSensorConnection = (connection: SensorWsConnection, closeSocket: boolean) => {
    connection.refCount = Math.max(0, connection.refCount - 1);
    if (closeSocket && connection.refCount === 0) {
        try {
            if (connection.socket && connection.socket.readyState <= WebSocket.CLOSING) {
                connection.socket.close();
            }
        } catch {
            // ignore
        }
        connection.socket = null;
    }
};

export const acquireSpectrumWsConnection = (
    wsHost: string,
    onStateChange: (state: WsState) => void,
): { connection: SensorWsConnection; release: () => void } => {
    const connection = ensureSensorConnection(wsHost, GLOBAL_SENSOR_KEY, onStateChange);
    connection.refCount += 1;

    return {
        connection,
        release: () => {
            connection.stateListeners.delete(onStateChange);
            releaseSensorConnection(connection, true);
        },
    };
};
